/**
 * Helpers shared by the map editor, its saved markup and the front end.
 *
 * Adapted from Getwid 3.0.2's map block (GPL-2.0-or-later).
 */

/**
 * Parse the markers stored as a JSON string in the mapMarkers attribute.
 *
 * @param {string} mapMarkers JSON-encoded markers.
 * @returns {Array} Markers, or an empty array when the value is empty or invalid.
 */
export function parseMapMarkers( mapMarkers ) {
	if ( ! mapMarkers ) {
		return [];
	}

	try {
		const parsed = JSON.parse( mapMarkers );

		return Array.isArray( parsed ) ? parsed : [];
	} catch ( error ) {
		return [];
	}
}

/**
 * Replace undefined values with empty strings, recursively.
 *
 * @param {*} value Value to normalise.
 * @returns {*} Normalised value.
 */
function normalizeUndefinedValues( value ) {
	if ( Array.isArray( value ) ) {
		return value.map( normalizeUndefinedValues );
	}

	if ( value !== null && typeof value === 'object' ) {
		return Object.fromEntries(
			Object.entries( value ).map( ( [ key, item ] ) => [ key, normalizeUndefinedValues( item ) ] )
		);
	}

	return typeof value === 'undefined' ? '' : value;
}

/**
 * Merge a partial marker into the marker at the given index.
 *
 * @param {Array}  markers Current markers.
 * @param {number} index   Index of the marker to update.
 * @param {object} value   Partial marker.
 * @returns {Array} Updated markers.
 */
export function updateMarkerAt( markers, index, value ) {
	const normalizedValue = normalizeUndefinedValues( value );

	return markers.map( ( marker, markerIndex ) => (
		index === markerIndex
			? {
				...marker,
				...normalizedValue,
				coords: {
					...marker.coords,
					...normalizedValue.coords,
				},
			}
			: marker
	) );
}

/**
 * Parse JSON without throwing.
 *
 * @param {string} value JSON string.
 * @returns {*} Parsed value, or null.
 */
function tryParseJson( value ) {
	try {
		return JSON.parse( value );
	} catch ( error ) {
		return null;
	}
}

/**
 * Parse a custom map style.
 *
 * Accepts JSON, or the JavaScript object literal syntax Getwid used to accept,
 * which is converted to JSON with regular expressions rather than evaluated.
 *
 * @param {string} customStyle Custom style, as entered in the editor.
 * @returns {Array|null} Google Maps styles, or null when the value is not usable.
 */
export function parseCustomMapStyle( customStyle ) {
	if ( typeof customStyle !== 'string' ) {
		return null;
	}

	const normalizedStyle = customStyle.trim();

	if ( normalizedStyle === '' ) {
		return null;
	}

	const parsedStyle = tryParseJson( normalizedStyle );

	if ( Array.isArray( parsedStyle ) ) {
		return parsedStyle;
	}

	if ( /(?:\bfunction\b|=>|\bwindow\b|\bdocument\b|\bthis\b|`|;)/.test( normalizedStyle ) ) {
		return null;
	}

	const legacyStyle = normalizedStyle
		.replace( /([{,]\s*)([A-Za-z_$][\w$]*)(\s*:)/g, '$1"$2"$3' )
		.replace( /'([^'\\]*(?:\\.[^'\\]*)*)'/g, ( match, inner ) => `"${ inner.replace( /"/g, '\\"' ) }"` )
		.replace( /,\s*([}\]])/g, '$1' );

	const parsedLegacyStyle = tryParseJson( legacyStyle );

	return Array.isArray( parsedLegacyStyle ) ? parsedLegacyStyle : null;
}

/**
 * Get the Google Maps styles for a map.
 *
 * @param {string} mapStyle    Either "default" or "custom".
 * @param {string} customStyle Custom style, used when mapStyle is "custom".
 * @returns {Array|null|undefined} Styles for the Google Maps `styles` option.
 */
export function getMapStyles( mapStyle, customStyle ) {
	if ( mapStyle === 'custom' ) {
		return parseCustomMapStyle( customStyle );
	}

	return undefined;
}
